/**
 * pages/ClientCollect.tsx
 *
 * Client form experience — premium mobile-first UX.
 *
 * Layout decisions:
 * - min-h-[100dvh] (dynamic viewport height) adapts to mobile keyboard.
 * - Content aligns to top on mobile (items-start) and centers on desktop (md:items-center).
 * - Progress bar uses sticky positioning.
 * - ClientToolbar (quit + save indicator) is hidden when the keyboard is open.
 * - viewportHeight constrains the question container when keyboard is open.
 *
 * Updated June 2026 (refactor):
 * - Removed SkippedFieldsBadge and all skipped-fields state.
 * - Uses viewportHeight from useVisualViewport to constrain the question container
 *   height when the mobile keyboard is open, keeping the card above the keyboard.
 * - SectionTransition no longer receives logoUrl/senderDisplayName props.
 */

import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Spinner from '../components/ui/Spinner';
import Card from '../components/ui/Card';
import SectionTransition from '../components/client/SectionTransition';
import WelcomeScreen from '../components/client/WelcomeScreen';
import ThankYouScreen from '../components/client/ThankYouScreen';
import QuestionCard from '../components/client/QuestionCard';
import SaveAndQuitScreen from '../components/client/SaveAndQuitScreen';
import ClientToolbar from '../components/client/ClientToolbar';
import ClientBrandingHeader from '../components/branding/ClientBrandingHeader';
import ClientBrandingFooter from '../components/branding/ClientBrandingFooter';
import { useDynamicDocumentMeta } from '../hooks/useDynamicDocumentMeta';
import { useClientForm } from '../hooks/useClientForm';
import { useVisualViewport } from '../hooks/useVisualViewport';
import { AlertCircle } from 'lucide-react';
import type { PreviewData } from '../utils/previewHelpers';

interface ClientCollectProps {
  previewMode?: boolean;
  previewData?: PreviewData;
}

export default function ClientCollect({ previewMode = false, previewData }: ClientCollectProps = {}) {
  const { token } = useParams<{ token: string }>();

  const form = useClientForm({ token, previewMode, previewData });
  const { isKeyboardOpen, viewportHeight } = useVisualViewport();

  useDynamicDocumentMeta({
    title: !previewMode
      ? (form.senderDisplayName
          ? `Questionnaire - ${form.senderDisplayName}`
          : 'Questionnaire')
      : null,
    faviconUrl: !previewMode ? form.logoUrl : null,
  });

  useEffect(() => {
    if (previewMode && previewData) {
      form.loadPreviewData();
    } else if (token) {
      form.loadRequest();
    }
  }, [token, previewMode, previewData]);

  const navRef = useRef({
    showWelcome: true,
    showSectionTransition: false,
    submitted: false,
    isLastStep: false,
    canProceed: () => false,
    handleNext: async () => {},
    handleSubmit: async () => {},
    setShowWelcome: (_: boolean) => {},
  });

  navRef.current = {
    showWelcome: form.showWelcome,
    showSectionTransition: form.showSectionTransition,
    submitted: form.submitted,
    isLastStep: form.isLastStep,
    canProceed: form.canProceed,
    handleNext: form.handleNext,
    handleSubmit: form.handleSubmit,
    setShowWelcome: form.setShowWelcome,
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const nav = navRef.current;
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') return;
      if (target.tagName === 'SELECT') return;
      if (nav.submitted) return;
      if (nav.showWelcome) {
        nav.setShowWelcome(false);
        return;
      }
      if (nav.showSectionTransition) return;
      if (!nav.canProceed()) return;
      (async () => {
        try {
          if (nav.isLastStep) await nav.handleSubmit();
          else await nav.handleNext();
        } catch (err) {
          console.error('Navigation error:', err);
        }
      })();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const heightClass = previewMode ? 'h-full' : 'min-h-[100dvh]';
  const progress = form.stepSequence.length > 0
    ? ((form.currentStep + 1) / form.stepSequence.length) * 100
    : 0;
  const hasBranding = !!(form.logoUrl || form.senderDisplayName);

  const isFormActive = !form.showWelcome && !form.submitted && !form.showQuitScreen;

  // When keyboard is open, constrain the question container to the visual viewport
  // so the card stays visible above the keyboard.
  const questionContainerStyle = isKeyboardOpen && viewportHeight > 0
    ? { height: `${viewportHeight - 4}px`, overflow: 'auto' as const }
    : undefined;

  if (form.loading) {
    return (
      <div className={`${heightClass} bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center`}>
        <div className="text-center">
          <div className="text-primary-600 mb-4 flex justify-center"><Spinner size="md" /></div>
          <p className="text-neutral-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (form.error && !form.request) {
    return (
      <div className={`${heightClass} bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4`}>
        <Card className="max-w-md">
          <div className="text-center">
            {hasBranding && (
              <div className="flex justify-center mb-6">
                <ClientBrandingHeader
                  logoUrl={form.logoUrl}
                  displayName={form.senderDisplayName}
                  size="md"
                />
              </div>
            )}
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-error-600" />
            </div>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Erreur</h2>
            <p className="text-neutral-600">{form.error}</p>
          </div>
        </Card>
      </div>
    );
  }

  const currentItem = form.stepSequence[form.currentStep];

  return (
    <div className={`${heightClass} ${previewMode ? 'overflow-y-auto' : ''} bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col`}>

      {/* Progress bar — sticky so it stays visible but doesn't cause viewport shift on mobile */}
      {isFormActive && (
        previewMode ? (
          <div className="flex-shrink-0 h-1 bg-neutral-100">
            <div
              className="h-full progress-bar-shimmer transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        ) : (
          <div className="sticky top-0 z-30 h-1 bg-neutral-100">
            <div
              className="h-full progress-bar-shimmer transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        )
      )}

      {/* Toolbar: quit + save indicator — hidden when keyboard is open */}
      {isFormActive && !previewMode && (
        <ClientToolbar
          isSaving={form.isSaving}
          isKeyboardOpen={isKeyboardOpen}
          onQuit={() => form.setShowQuitScreen(true)}
        />
      )}

      {/* Branding header */}
      {isFormActive && hasBranding && !isKeyboardOpen && (
        <div className={`flex-shrink-0 flex items-center px-5 ${previewMode ? 'py-3' : 'pt-4 pb-2'}`}>
          <ClientBrandingHeader
            logoUrl={form.logoUrl}
            displayName={form.senderDisplayName}
            size="md"
          />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {form.submitted ? (
          <ThankYouScreen logoUrl={form.logoUrl} senderDisplayName={form.senderDisplayName} />
        ) : form.showQuitScreen ? (
          <SaveAndQuitScreen logoUrl={form.logoUrl} senderDisplayName={form.senderDisplayName} />
        ) : form.showWelcome ? (
          <WelcomeScreen
            title={form.request?.title || ''}
            customMessage={form.request?.custom_message || null}
            logoUrl={form.logoUrl}
            senderDisplayName={form.senderDisplayName}
            onStart={() => form.setShowWelcome(false)}
          />
        ) : form.showSectionTransition && form.currentSection ? (
          <SectionTransition
            sectionKey={form.currentSection}
            onContinue={form.handleContinueAfterTransition}
          />
        ) : currentItem?.kind === 'field' ? (
          <div
            className="flex-1 flex items-start md:items-center justify-center px-4 pt-4 pb-8 md:py-8"
            style={questionContainerStyle}
          >
            <QuestionCard
              key={form.currentStep}
              field={currentItem.field}
              response={form.responses[currentItem.field.id] ?? ''}
              uploadedFiles={form.uploadedFiles[currentItem.field.id] ?? []}
              direction={form.direction}
              currentStep={form.currentStep}
              isLastStep={form.isLastStep}
              canProceed={form.canProceed()}
              submitting={form.submitting}
              error={form.error}
              onResponseChange={(val) => form.handleResponseChange(currentItem.field.id, val)}
              onFilesSelect={(files) => form.handleFileSelect(currentItem.field.id, files)}
              onFileRemove={(index) => form.handleFileRemove(currentItem.field.id, index)}
              onFileRetry={(index) => form.retryFileUpload(currentItem.field.id, index)}
              onPrevious={form.handlePrevious}
              onNext={form.handleNext}
              onSubmit={form.handleSubmit}
              onAutoAdvance={form.handleNext}
            />
          </div>
        ) : null}
      </div>

      {/* Footer branding */}
      {isFormActive && !isKeyboardOpen && (
        <ClientBrandingFooter displayName={form.senderDisplayName} />
      )}
    </div>
  );
}
